import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';

import ImportDataModal from 'modals/ImportDataModal';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import PATHS, { mergePaths } from 'utils/paths';

import { faDownload, faPlus } from '@fortawesome/pro-solid-svg-icons';

import CityBasedPriceForm from '../CityBasedPrices/CityBasedPriceForm';
import KMBasedPriceForm from '../KMBasedPrices/KMBasedPriceForm';
import TierBasedPriceForm from '../TierBasedPrices/TierBasedPriceForm';
import WeightBasedPriceForm from '../WeightBasedPrices/WeightBasedPriceForm';
import WeightCountryBasedPriceForm from '../WeightCountryBasedPrices/WeightCountryBasedPriceForm';

/**
 * @author @muhammedburakak
 */

const PriceConfigurations = () => {
	const { pathname } = useLocation();

	const tabs = useMemo(
		() => [
			{
				label: 'tierBasedPrices',
				path: mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.TIER_BASED_PRICES)
			},
			{
				label: 'weightBasedPrices',
				path: mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.WEIGHT_BASED_PRICES)
			},
			{
				label: 'weightCountryBasedPrices',
				path: mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.WEIGHT_COUNTRY_BASED_PRICES)
			},
			{
				label: 'cityBasedPrices',
				path: mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.CITY_BASED_PRICES)
			},
			{
				label: 'KMBasedPrices',
				path: mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.KM_BASED_PRICES)
			}
		],
		[]
	);

	return (
		<div>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/calculator-icon.json.gz"
				title="priceConfigurations">
				{/* Import button for each tab */}
				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.TIER_BASED_PRICES) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.tierBasedPriceImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.WEIGHT_BASED_PRICES) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.weightBasedPriceImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.WEIGHT_COUNTRY_BASED_PRICES) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.weightCountryBasedPriceImport)}
					/>
				)}

				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.CITY_BASED_PRICES) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.cityBasedPriceImport)}
					/>
				)}
				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.KM_BASED_PRICES) && (
					<Button
						className="mobile-hidden importDataButton"
						icon={faDownload}
						variant="borderless"
						label="importData"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() => NiceModal.show(ImportDataModal, importActions.kmBasedPriceImport)}
					/>
				)}

				{/* Add button for each tab */}
				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.TIER_BASED_PRICES) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addTierBasedPrice"
						onClick={() => NiceModal.show(TierBasedPriceForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.WEIGHT_BASED_PRICES) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addWeightBasedPrice"
						onClick={() => NiceModal.show(WeightBasedPriceForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.WEIGHT_COUNTRY_BASED_PRICES) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addWeightCountryBasedPrice"
						onClick={() => NiceModal.show(WeightCountryBasedPriceForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.CITY_BASED_PRICES) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addCityBasedPrice"
						onClick={() => NiceModal.show(CityBasedPriceForm)}
						variant="blue"
						className="addButton"
					/>
				)}

				{pathname === mergePaths(PATHS.PRICE_CONFIGURATIONS, PATHS.KM_BASED_PRICES) && (
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addKMBasedPrice"
						onClick={() => NiceModal.show(KMBasedPriceForm)}
						variant="blue"
						className="addButton"
					/>
				)}
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default PriceConfigurations;
